import { Controller } from "@hotwired/stimulus"

/**
 * SageMaker Details Controller
 * 
 * Manages the loading and display of SageMaker job details in admin interface.
 * Handles AJAX requests to fetch job details and displays them in expandable rows.
 * 
 * Features:
 * - Loads SageMaker job details via AJAX
 * - Shows loading states during requests
 * - Displays job details in expandable table rows
 * - Handles error states and displays error messages
 * - Supports both job ID and job name identifiers
 * 
 * Usage: Add data-controller="sagemaker-details" to SageMaker job tables with detail buttons
 */
export default class extends Controller {
  static targets = ["detailsRow", "button"]
  static values = { debug: Boolean }
  
  connect() {}
  
  async loadDetails(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const jobId = button.dataset.jobId
    const jobName = button.dataset.jobName
    const jobIdentifier = jobId || jobName

    if (!jobIdentifier) {
      console.error('No job ID or job name found')
      return
    }
    
    const detailsRow = document.getElementById(`details-${jobIdentifier}`)
    
    if (!detailsRow) {
      console.error(`Details row not found for job ${jobIdentifier}`)
      return
    }
    
    // Show loading state
    button.disabled = true
    button.innerHTML = '<i class="bi bi-hourglass-split"></i> Loading...'
    detailsRow.style.display = 'table-row'
    
    try {
      const endpoint = jobId ? `/admin/sage_maker/job_details/${jobId}` : `/admin/sage_maker/job_details_ajax/${jobName}`
      const response = await fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        this.displayDetails(detailsRow, data.details)
      } else {
        this.displayError(detailsRow, data.message || 'Failed to load details')
      }
    } catch (error) {
      console.error('Error loading SageMaker details:', error)
      this.displayError(detailsRow, 'Error loading details')
    } finally {
      // Reset button state
      button.disabled = false
      button.innerHTML = '<i class="bi bi-info-circle"></i> SageMaker Details'
    }
  }
  
  displayDetails(detailsRow, details) {
    const duration = details.total_duration_minutes ? `${details.total_duration_minutes}m` : 'N/A'
    const bootTime = details.boot_time_minutes ? `${details.boot_time_minutes}m` : 'N/A'
    const status = details.sagemaker_status || 'Unknown'
    
    // Determine colspan based on the table structure
    const colspan = detailsRow.querySelector('td').colSpan || 6
    
    detailsRow.innerHTML = `
      <td colspan="${colspan}">
        <div class="card">
          <div class="card-body">
            <h6 class="card-title">SageMaker Infrastructure Details</h6>
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm">
                  <tr>
                    <td><strong>Job Name:</strong></td>
                    <td>${details.sagemaker_job_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>SageMaker Status:</strong></td>
                    <td><span class="badge badge-${this.getStatusBadgeClass(status)}">${status}</span></td>
                  </tr>
                  <tr>
                    <td><strong>Instance Type:</strong></td>
                    <td>${details.instance_type || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td><strong>Instance Count:</strong></td>
                    <td>${details.instance_count || 'N/A'}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6">
                <table class="table table-sm">
                  <tr>
                    <td><strong>Total Duration:</strong></td>
                    <td>${duration}</td>
                  </tr>
                  <tr>
                    <td><strong>Boot Time:</strong></td>
                    <td>${bootTime}</td>
                  </tr>
                  <tr>
                    <td><strong>Created:</strong></td>
                    <td>${this.formatDateTime(details.sagemaker_created_at)}</td>
                  </tr>
                  <tr>
                    <td><strong>Completed:</strong></td>
                    <td>${this.formatDateTime(details.sagemaker_completed_at)}</td>
                  </tr>
                </table>
              </div>
            </div>
            ${details.failure_reason ? `
            <div class="alert alert-danger mt-3">
              <strong>Failure Reason:</strong> ${details.failure_reason}
            </div>
            ` : ''}
          </div>
        </div>
      </td>
    `
  }
  
  displayError(detailsRow, message) {
    // Determine colspan based on the table structure
    const colspan = detailsRow.querySelector('td').colSpan || 6
    
    detailsRow.innerHTML = `
      <td colspan="${colspan}">
        <div class="alert alert-warning">
          <i class="bi bi-exclamation-triangle"></i> ${message}
        </div>
      </td>
    `
  }
  
  getStatusBadgeClass(status) {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'failed':
        return 'danger'
      case 'inprogress':
        return 'info'
      case 'stopped':
        return 'warning'
      default:
        return 'secondary'
    }
  }
  
  formatDateTime(dateTimeString) {
    if (!dateTimeString) return 'N/A'
    try {
      const date = new Date(dateTimeString)
      return date.toLocaleString()
    } catch (e) {
      return dateTimeString
    }
  }
} 